import React, { useEffect, useState, useRef, createElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, MoreHorizontalIcon, UsersIcon, PlusIcon, LinkIcon, TrashIcon, EditIcon, CheckIcon, CopyIcon, DownloadIcon, Edit2Icon } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import EmptyState from '../components/EmptyState';
import { AddLearnersModal } from '../components/AddLearnersModal';
import { EditGroupModal } from '../components/EditGroupModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
// Mock Data
const GROUP_DATA = {
  id: '1',
  name: 'Remedial Math Group A',
  description: 'Extra support for algebra concepts',
  createdBy: 'Sarah Johnson',
  createdOn: 'Mar 15, 2024',
  joinCode: 'RMG-442',
  stats: {
    learners: 8
  }
};
const LEARNERS_DATA = [{
  id: 1,
  name: 'Alice Johnson',
  email: 'alice@school.edu',
  status: 'Joined',
  joinedAt: '2024-03-16'
}, {
  id: 2,
  name: 'Bob Smith',
  email: 'bob@school.edu',
  status: 'Invited',
  joinedAt: '-'
}, {
  id: 3,
  name: 'Charlie Brown',
  email: 'charlie@school.edu',
  status: 'Joined',
  joinedAt: '2024-03-17'
}, {
  id: 4,
  name: 'Diana Martinez',
  email: 'diana@school.edu',
  status: 'Joined',
  joinedAt: '2024-03-16'
}, {
  id: 5,
  name: 'Ethan Davis',
  email: 'ethan@school.edu',
  status: 'Joined',
  joinedAt: '2024-03-18'
}, {
  id: 6,
  name: 'Fiona Wilson',
  email: 'fiona@school.edu',
  status: 'Invited',
  joinedAt: '-'
}, {
  id: 7,
  name: 'George Taylor',
  email: 'george@school.edu',
  status: 'Joined',
  joinedAt: '2024-03-17'
}, {
  id: 8,
  name: 'Hannah Lee',
  email: 'hannah@school.edu',
  status: 'Joined',
  joinedAt: '2024-03-19'
}];
export function GroupDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [isAddLearnerOpen, setIsAddLearnerOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [groupData, setGroupData] = useState(GROUP_DATA);
  const [linkCopied, setLinkCopied] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };
    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreMenuOpen]);
  const handleEditGroup = async (data: any) => {
    console.log('Updating group:', data);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setGroupData(prev => ({
      ...prev,
      name: data.name,
      description: data.description
    }));
    setIsEditModalOpen(false);
  };
  const handleCopyJoinLink = () => {
    const link = `https://app.ainstein.com/join/${groupData.joinCode}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  const handleExportRoster = () => {
    console.log('Exporting roster...');
    const csvContent = 'Name,Email,Status,Joined At\n' + LEARNERS_DATA.map(l => `${l.name},${l.email},${l.status},${l.joinedAt}`).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${groupData.name}_roster.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsMoreMenuOpen(false);
  };
  const handleDeleteGroup = async () => {
    console.log('Deleting group...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    navigate('/classes');
  };
  return <div className="flex flex-col min-h-screen bg-background" data-id="element-2965">
      <Header data-id="element-2966" />
      <div className="flex flex-1 overflow-hidden" data-id="element-2967">
        <div className="hidden md:block" data-id="element-2968">
          <Sidebar data-id="element-2969" />
        </div>
        <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-2970">
          <main className="flex-1" data-id="element-2971">
            {/* Breadcrumb & Header */}
            <div className="bg-white border-b border-custom px-6 py-6" data-id="element-2972">
              <div className="max-w-7xl mx-auto" data-id="element-2973">
                <button onClick={() => navigate('/classes')} className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors mb-4" data-id="element-2974">
                  <ArrowLeftIcon size={16} data-id="element-2975" />
                  Back to Groups
                </button>

                <div className="flex items-start justify-between" data-id="element-2976">
                  <div className="flex items-start gap-4" data-id="element-2977">
                    <div className="w-14 h-14 rounded-xl bg-[#00B75F]/10 flex items-center justify-center text-[#00B75F]" data-id="element-2978">
                      <div size={28} data-id="element-2979" />
                    </div>
                    <div data-id="element-2980">
                      <div className="flex items-center gap-3 mb-2" data-id="element-2981">
                        <h1 className="text-3xl font-bold text-text-primary" data-id="element-2982">
                          {groupData.name}
                        </h1>
                        <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-purple-100 text-purple-700 border border-purple-200 uppercase tracking-wide" data-id="element-2983">
                          Group
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary mb-1" data-id="element-2984">
                        {groupData.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-text-tertiary" data-id="element-2985">
                        <span data-id="element-2986">Created by {groupData.createdBy}</span>
                        <span data-id="element-2987">•</span>
                        <span data-id="element-2988">{groupData.createdOn}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-2989">
                    <button onClick={() => setIsEditModalOpen(true)} className="px-4 py-2 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-2990">
                      <Edit2Icon size={16} data-id="element-2991" />
                      Edit Details
                    </button>

                    {/* More Menu */}
                    <div className="relative" ref={moreMenuRef} data-id="element-2992">
                      <button onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)} className="p-2 border border-custom rounded-xl text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-2993">
                        <MoreHorizontalIcon size={20} data-id="element-2994" />
                      </button>

                      <AnimatePresence data-id="element-2995">
                        {isMoreMenuOpen && <motion.div initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className="absolute right-0 mt-2 w-56 bg-white border border-custom rounded-xl shadow-lg overflow-hidden z-50" data-id="element-2996">
                            <button onClick={handleExportRoster} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-text-primary hover:bg-gray-50 transition-colors" data-id="element-2997">
                              <DownloadIcon size={16} data-id="element-2998" />
                              Export Roster
                            </button>
                            <button onClick={handleCopyJoinLink} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-text-primary hover:bg-gray-50 transition-colors border-t border-gray-100" data-id="element-2999">
                              <LinkIcon size={16} data-id="element-3000" />
                              Copy Join Link
                            </button>
                            <button onClick={() => {
                          setIsDeleteModalOpen(true);
                          setIsMoreMenuOpen(false);
                        }} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100" data-id="element-3001">
                              <TrashIcon size={16} data-id="element-3002" />
                              Delete Group
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-3003">
              {/* Stats Card */}
              <div className="bg-white p-6 rounded-2xl border border-custom shadow-sm mb-8" data-id="element-3004">
                <div className="flex items-center gap-4" data-id="element-3005">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-3006">
                    <UsersIcon size={24} data-id="element-3007" />
                  </div>
                  <div data-id="element-3008">
                    <p className="text-sm text-text-secondary" data-id="element-3009">
                      Total Learners
                    </p>
                    <p className="text-2xl font-bold text-text-primary" data-id="element-3010">
                      {groupData.stats.learners}
                    </p>
                  </div>
                </div>
              </div>

              {/* Learners Section */}
              <div data-id="element-3011">
                <div className="flex items-center justify-between mb-6" data-id="element-3012">
                  <div data-id="element-3013">
                    <h2 className="text-xl font-bold text-text-primary" data-id="element-3014">
                      Group Members
                    </h2>
                    <p className="text-sm text-text-secondary mt-1" data-id="element-3015">
                      Manage learners in this group
                    </p>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3016">
                    <button onClick={handleCopyJoinLink} className="px-4 py-2 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-3017">
                      {linkCopied ? <>
                          <CheckIcon size={16} className="text-[#00B75F]" data-id="element-3018" />
                          <span className="text-[#00B75F]" data-id="element-3019">Copied!</span>
                        </> : <>
                          <LinkIcon size={16} data-id="element-3020" />
                          Copy Link
                        </>}
                    </button>
                    <button onClick={() => setIsAddLearnerOpen(true)} className="px-4 py-2 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors flex items-center gap-2" data-id="element-3021">
                      <PlusIcon size={16} data-id="element-3022" />
                      Add Learners
                    </button>
                  </div>
                </div>

                {LEARNERS_DATA.length > 0 ? <div className="bg-white border border-custom rounded-xl overflow-hidden" data-id="element-3023">
                    <table className="w-full" data-id="element-3024">
                      <thead className="bg-gray-50 border-b border-custom" data-id="element-3025">
                        <tr data-id="element-3026">
                          <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3027">
                            Name
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3028">
                            Email / ID
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3029">
                            Status
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3030">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-3031">
                        {LEARNERS_DATA.map(learner => <tr key={learner.id} className="hover:bg-gray-50 transition-colors" data-id="element-3032">
                            <td className="px-6 py-4" data-id="element-3033">
                              <span className="font-medium text-text-primary" data-id="element-3034">
                                {learner.name}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-text-secondary" data-id="element-3035">
                              {learner.email}
                            </td>
                            <td className="px-6 py-4" data-id="element-3036">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${learner.status === 'Joined' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-3037">
                                {learner.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right" data-id="element-3038">
                              <div className="flex items-center justify-end gap-2" data-id="element-3039">
                                <button className="p-2 text-text-tertiary hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-3040">
                                  <TrashIcon size={16} data-id="element-3041" />
                                </button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div> : <EmptyState icon={<UsersIcon size={48} className="text-text-tertiary" data-id="element-3043" />} title="No learners added yet" description="Add learners to this group to get started." actionLabel="Add Learners" onAction={() => setIsAddLearnerOpen(true)} data-id="element-3042" />}
              </div>
            </div>
          </main>
          <Footer data-id="element-3044" />
        </div>
      </div>

      <AddLearnersModal isOpen={isAddLearnerOpen} onClose={() => setIsAddLearnerOpen(false)} classroomName={groupData.name} joinCode={groupData.joinCode} data-id="element-3045" />

      <EditGroupModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleEditGroup} group={{
      name: groupData.name,
      description: groupData.description
    }} data-id="element-3046" />

      <ConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleDeleteGroup} title="Delete Group?" description="This group will be permanently deleted. Learners will no longer have access." confirmLabel="Delete Group" type="danger" data-id="element-3047" />
    </div>;
}